import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { TmCheckCapture, TmCheckCaptureManual } from '@treasury/domain-components';
import { DeviceService } from '@treasury/domain/services/device/device.service';
import { MitekAutoCaptureHints, MitekCaptureResult } from '@treasury/libraries';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-footer';
import { LocalStorageService } from '@treasury/utils/services';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import {
    CancelCapturePayload,
    ChangeCheckSidePayload,
    ProceedToCreatePayload,
    RemoteDepositEvent,
} from '../containers/remote-deposit.types';

export const tagName = 'capture-step';

/**
 * @fires CaptureStep#proceed - Event fired when the user clicks the review button
 * @fires CaptureStep#change-check-side - Event fired when the user proceeds to the back of the check
 */
@customElement(tagName)
export class CaptureStep extends TmBaseComponent {
    public static styles = css`
        :host {
            flex-grow: 1;
        }
    `;

    @InjectProperty()
    declare private readonly notificationService: NotificationService;

    @InjectProperty()
    declare private readonly deviceService: DeviceService;

    @InjectProperty()
    declare private readonly navigationService: NavigationService;

    @InjectProperty()
    declare private readonly localStorageService: LocalStorageService;

    @state()
    private frontImage?: string;

    @state()
    private rearImage?: string;

    @state()
    private currentSide: 'front' | 'back' = 'front';

    private checkCaptureRef: Ref<TmCheckCapture | TmCheckCaptureManual> = createRef();

    protected render() {
        return html`<tm-section class="grow">
                <tm-check-capture
                    ${ref(this.checkCaptureRef)}
                    .checkSide=${this.currentSide}
                    .photo=${this.currentSide === 'front' ? this.frontImage : this.rearImage}
                    @image-uploaded=${this.receiveImage}
                    @image-capture-failed=${this.onCaptureFailed.bind(this)}
                    @camera-start-timeout=${(event: CustomEvent) => {
                        this.notificationService.renderError(event.detail);
                        this.navigationService.navigate('/deposit');
                    }}
                    @close=${() =>
                        this.dispatchEvent(
                            new CustomEvent<CancelCapturePayload>(RemoteDepositEvent.CancelCapture)
                        )}
                ></tm-check-capture>
            </tm-section>
            <tm-footer
                class="bg-black"
                direction="column"
                .showTopShadow=${false}
                .buttonConfig=${[
                    {
                        text: this.currentSide === 'front' ? 'Continue to Back' : 'Finish',
                        classes: 'w-full max-w-40',
                        disabled: this.currentSide === 'front' ? !this.frontImage : !this.rearImage,
                        onClick: () => this.continue(),
                    },
                    {
                        text: 'Retake',
                        importance: 'secondary',
                        classes: 'w-full max-w-40',
                        onClick: () => this.restartImage(),
                    },
                    {
                        text: 'Cancel',
                        importance: 'secondary',
                        classes: 'w-full max-w-40',
                        onClick: () => this.cancelCapture(),
                    },
                ]}
            >
            </tm-footer>`;
    }

    private onCaptureFailed({ detail: { response } }: CustomEvent<MitekCaptureResult>) {
        if (response) {
            const warningKey = response?.warnings.key as keyof typeof MitekAutoCaptureHints;
            let errorMessage;
            if (warningKey === 'CAMERA_PERMISSION_DENIED') {
                const isApple = this.deviceService.osName === 'iOS';
                errorMessage = `We have detected that you have turned off camera permissions for web sites. Please go to ${isApple ? 'Settings, Safari, Camera' : 'your device settings'} and set the camera access to either Ask or Allow (we recommend Ask).`;
                this.navigationService.navigate('/deposit');
            } else {
                errorMessage = `${response.mode === 'AUTO' ? 'Image' : 'Manual'} capture failed: ${MitekAutoCaptureHints[warningKey]}`;
            }
            this.notificationService.renderError(new Error(errorMessage));
        }
    }

    private cancelCapture() {
        this.dispatchEvent(new CustomEvent<CancelCapturePayload>(RemoteDepositEvent.CancelCapture));
    }

    private receiveImage({ detail }: CustomEvent) {
        const { checkSide, image } = detail;
        if (checkSide === 'front') {
            this.frontImage = image;
        } else {
            this.rearImage = image;
        }
    }

    private restartImage() {
        if (this.currentSide === 'front') {
            this.frontImage = undefined;
        } else {
            this.rearImage = undefined;
        }
        this.checkCaptureRef.value?.startCapture();
    }

    private continue() {
        if (this.currentSide === 'front') {
            this.currentSide = 'back';
            if (this.checkCaptureRef.value) {
                this.checkCaptureRef.value.checkSide = 'back';
                this.checkCaptureRef.value.startCapture();
            }
            this.dispatchEvent(
                new CustomEvent<ChangeCheckSidePayload>(RemoteDepositEvent.ChangeCheckSide)
            );
        } else {
            this.dispatchEvent(
                new CustomEvent<ProceedToCreatePayload>(RemoteDepositEvent.ProceedToCreate, {
                    detail: { frontImage: this.frontImage, rearImage: this.rearImage },
                })
            );
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CaptureStep;
    }
}
